/**
 * Draft state for the "Import Procedure Template" dialog (z8nrz7dz4g).
 *
 * The sibling of `procedureTemplateCopy.ts`, and split the same way: the
 * DECISIONS — what is reused, what is created, what cannot be resolved — are
 * made server-side by `transfer/resolve-import.ts`, because only the server can
 * see the target tenant's materials, equipment and handles. What is left for
 * the client is reading a file, holding the names the user edits, and turning a
 * plan into something a dialog can render.
 *
 * Pure, and in `src/lib/` rather than inline in the dialog, because
 * `vitest.config.ts` is `environment: node` with `include: ['src/**\/*.test.ts']`
 * — logic expressed inside a `.tsx` could never be tested.
 */

import type {
  ProcedureTemplateBundle,
  ProcedureTemplateImportChoices,
  ProcedureTemplateImportPlan,
} from '@/types/procedureTemplateTransfer'

/** What the dialog holds between picking a file and committing. */
export interface ImportDraft {
  fileName: string
  /** Passed back to the server untouched; the client never edits a bundle. */
  bundle: ProcedureTemplateBundle
  /** Name for the imported template. Seeded from the bundle. */
  templateName: string
  /** Name per CREATED form, keyed by bundle handle. Reused forms are absent. */
  formNames: Record<string, string>
  asNewVersion: boolean
  applyProductCode: boolean
}

/**
 * Read a picked file into a draft.
 *
 * Only checks that the text is JSON at all. Whether it is a BUNDLE — and a
 * version this environment reads — is the server's call, deliberately: that
 * check has to exist server-side regardless (a client cannot be trusted with
 * it), and duplicating it here would leave two definitions of "a valid bundle"
 * to drift apart.
 */
export function readBundleFile(
  fileName: string,
  text: string,
): { ok: true; draft: ImportDraft } | { ok: false; error: string } {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return { ok: false, error: 'That file is not valid JSON.' }
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: 'That file is not a procedure-template bundle.' }
  }

  const bundle = parsed as ProcedureTemplateBundle
  return {
    ok: true,
    draft: {
      fileName,
      bundle,
      templateName: bundle.template?.name ?? '',
      formNames: {},
      asNewVersion: false,
      applyProductCode: false,
    },
  }
}

/** The request body `previewImport` / `commitImport` take. */
export function buildImportChoices(draft: ImportDraft): ProcedureTemplateImportChoices {
  return {
    templateName: draft.templateName.trim(),
    // Only names the user actually changed. Sending every form's default would
    // work, but an empty object makes it obvious in a request log that nothing
    // was renamed.
    formNames: Object.fromEntries(
      Object.entries(draft.formNames)
        .map(([handle, name]) => [handle, name.trim()] as const)
        .filter(([, name]) => name.length > 0),
    ),
    asNewVersion: draft.asNewVersion,
    applyProductCode: draft.applyProductCode,
  }
}

/** Replaces one form's name, leaving the rest of the draft alone. */
export function setImportFormName(
  draft: ImportDraft,
  handle: string,
  name: string,
): ImportDraft {
  return { ...draft, formNames: { ...draft.formNames, [handle]: name } }
}

/**
 * Blocker messages keyed by the row they belong to — `'template'` or a form
 * handle — so the dialog can put each one under the input that caused it.
 *
 * Keyed rather than one shared error line for the reason the copy dialog gives:
 * with a single line, a user looking at eight forms cannot tell which is the
 * problem. A blocker naming no row falls through to `'template'`, which is the
 * right home for everything else.
 */
export function blockersByRow(plan: ProcedureTemplateImportPlan): Record<string, string[]> {
  const byRow: Record<string, string[]> = {}
  for (const blocker of plan.blockers) {
    const key = blocker.formHandle ?? 'template'
    ;(byRow[key] ??= []).push(blocker.message)
  }
  return byRow
}

/** Notes keyed the same way, so a row can show why it is being reused. */
export function notesByRow(plan: ProcedureTemplateImportPlan): Record<string, string[]> {
  const byRow: Record<string, string[]> = {}
  for (const note of plan.notes) {
    const key = note.formHandle ?? 'template'
    ;(byRow[key] ??= []).push(note.message)
  }
  return byRow
}

export interface ImportSummary {
  formsCreated: number
  formsReused: number
  blockerCount: number
  /** One line, for the header of the preview. */
  headline: string
}

/**
 * The one-line verdict at the top of the preview.
 *
 * States the REFUSAL first when there is one. A preview that led with "12 forms
 * will be created" and mentioned four blockers further down would read as
 * success to anyone skimming.
 */
export function summarizeImport(plan: ProcedureTemplateImportPlan): ImportSummary {
  const formsCreated = plan.forms.filter(f => f.action === 'create').length
  const formsReused = plan.forms.filter(f => f.action === 'reuse').length
  const blockerCount = plan.blockers.length

  let headline: string
  if (blockerCount > 0) {
    headline =
      blockerCount === 1
        ? 'This bundle cannot be imported here — 1 reference could not be resolved.'
        : `This bundle cannot be imported here — ${blockerCount} references could not be resolved.`
  } else {
    const parts = [
      plan.template.action === 'new-version'
        ? `${plan.template.name} V${plan.template.version}`
        : `${plan.template.name} (new template)`,
    ]
    if (formsCreated > 0) parts.push(`${formsCreated} ${plural(formsCreated, 'form')} created`)
    if (formsReused > 0) parts.push(`${formsReused} reused`)
    headline = parts.join(' · ')
  }

  return { formsCreated, formsReused, blockerCount, headline }
}

function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`
}

/** Triggers a browser download of a bundle. */
export function downloadBundle(fileName: string, bundle: unknown): void {
  // Pretty-printed on purpose: a bundle is meant to be reviewable and diffable
  // in a pull request, which a single-line JSON blob is not.
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}
